import { leafGlassSurcharge } from '../data/leafGlass.js';
import { thresholdForFamily } from '../data/smartline.js';

// Deterministic B2C pricing engine — JavaScript mirror of the Postgres
// function calculate_b2c_price() in supabase/migrations/013 + 014.
//
// The backend RPC is authoritative when the pricing tables exist; this module
// is the local fallback AND the implementation the admin Price Tester uses to
// show the full staged breakdown. Same inputs => same outputs in both engines.
//
// Input:  { family, qty, variant, width_mm, height_mm, design, colour, glass,
//           frame, threshold, lock, hinge, sidelight, extras: [], hinges,
//           opening, handing,
//           configuration_type, left_sidelight, right_sidelight,
//           sidelight_same, sidelight_width_mm, sidelight_height_mm }
//         left/right_sidelight: { width_mm, height_mm, glass_type, layout }
// Data:   { families, priceList, tiers, factors, rules, standardOptions,
//           overrides, sidelight }
//         priceList { code, version, currency, rounding_mode, rounding_step }
//         tiers     [{ family_id, variant, qty_min, qty_max, price, data_status }]
//         factors   [{ scope, scope_key, operation, value, label, enabled, sort }]
//                   scope 'sidelight' applies to sidelight subtotals only.
//         rules     [{ family_id|null, rule_kind, target, meta, qty_min, qty_max,
//                      op:'add'|'pct', value, unit:'door'|'hinge', label,
//                      data_status, charge_scope, component_scope }]
//                   charge_scope: PER_DOOR_LEAF (default) | PER_DOOR_SET |
//                                 PER_SIDELIGHT
//         standardOptions [optionId] — surcharge-free colour set
//         overrides [{ family_id, variant, qty_min, qty_max, price, active }]
//         sidelight {
//           families   { familyId: enabled }
//           base       [{ family_id|null, variant, base_price, enabled }]
//           glass      [{ glass_type, price_per_m2, enabled, families|null }]
//           dividers   [{ divider_type, fixed_price, price_per_lm, enabled }]
//           widths     [{ width_mm, enabled }]
//           settings   { min_width_mm, max_width_mm, min_height_mm,
//                        max_height_mm, deduction_left_mm, deduction_right_mm,
//                        deduction_top_mm, deduction_bottom_mm,
//                        deduction_divider_mm, custom_width_mode,
//                        special_size_op, special_size_value, height_default }
//         }
//
// Stages: BASE → OPTION_ADJUSTMENTS (door) → SIDELIGHT (per component)
//         → RETAIL_FACTORS (door: global→family→variant→option→size;
//           sidelight: global→sidelight) → RETAIL_OVERRIDE → ROUNDING
//
// Output: { ok, family, variant, configurationType, qty, sidelightCount,
//           retailPrice, totalPrice, currency, confidence, requiresReview,
//           reasons[], priceList, subtotalNet, breakdown[] }
//
// A configured sidelight whose price is incomplete yields retailPrice=null +
// requiresReview — never a partial-looking total.

const round2 = (n) => Math.round(n * 100) / 100;

const tierMatch = (r, qty) => (r.qty_min == null || r.qty_min <= qty) && (r.qty_max == null || r.qty_max >= qty);

const SCOPE_ORDER = { global: 0, family: 1, variant: 2, option: 3, size: 4, sidelight: 5 };

const LAYOUT_DIVIDERS = { FULL_GLASS: 0, ONE_HORIZONTAL_DIVIDER: 1, TWO_HORIZONTAL_DIVIDERS: 2 };

const SL_DEFAULTS = {
  min_width_mm: 200, max_width_mm: 1200, min_height_mm: 600, max_height_mm: 2400,
  deduction_left_mm: 40, deduction_right_mm: 40, deduction_top_mm: 40,
  deduction_bottom_mm: 40, deduction_divider_mm: 40,
  custom_width_mode: 'manual', special_size_op: 'manual', special_size_value: null,
  height_default: 'door'
};

const numSetting = (settings, key) => {
  const v = settings?.[key];
  const n = Number(v);
  return Number.isFinite(n) ? n : SL_DEFAULTS[key];
};

export function calculateDoorPrice(input, data) {
  const { families, priceList, tiers, factors, rules, standardOptions, overrides } = data;
  const sl = data.sidelight || {};
  const slSet = { ...SL_DEFAULTS, ...(sl.settings || {}) };
  const reasons = [];
  const breakdown = [];
  let confidence = 'high';
  let requiresReview = false;

  const family = families.find((f) => f.id === input.family);
  if (!family) {
    return { ok: false, error: 'unknown_family', requiresReview: true, confidence: 'unpriced', retailPrice: null };
  }

  input = { ...input, threshold: thresholdForFamily(family.id, input.threshold) };
  const glassSurcharge = leafGlassSurcharge(family.id, input.glass);
  const qty = Math.max(Number(input.qty) || 1, 1);
  const hinges = Math.max(Number(input.hinges) || 3, 1);
  const w = Number(input.width_mm);
  const h = Number(input.height_mm);

  // Configuration type — new field wins; legacy `sidelight` option maps.
  let configurationType = (input.configuration_type || '').toUpperCase()
    || ({ sidelight_left: 'DOOR_LEFT_SIDELIGHT', sidelight_right: 'DOOR_RIGHT_SIDELIGHT', sidelight_both: 'DOOR_TWO_SIDELIGHTS' }[input.sidelight])
    || null;
  if (input.design === 'design_double' && !configurationType.startsWith('DOUBLE_DOOR')) {
    configurationType = 'DOUBLE_DOOR';
  }
  if (!configurationType) configurationType = 'SINGLE_DOOR';
  const doorUnits = configurationType.startsWith('DOUBLE_DOOR') ? 2 : 1;

  const sides = [
    ...(configurationType.includes('LEFT_SIDELIGHT') || configurationType.includes('TWO_SIDELIGHTS') ? ['left'] : []),
    ...(configurationType.includes('RIGHT_SIDELIGHT') || configurationType.includes('TWO_SIDELIGHTS') ? ['right'] : [])
  ];
  const sidelightCount = sides.length;

  if (family.opening !== 'both' && (input.opening || family.opening) !== family.opening) {
    requiresReview = true;
    reasons.push('opening_direction_not_compatible');
  }

  // Variant resolution — MERANTI is priced per glazing variant.
  const variant = input.variant
    || (family.id === 'MERANTI'
      ? ((input.glass || 'glass_none') !== 'glass_none' ? 'with_glass' : 'no_glass')
      : 'default');

  const nonstd = !w || !h || w < family.minWidth || w > family.maxWidth || h < family.minHeight || h > family.maxHeight;
  if (nonstd) {
    requiresReview = true;
    reasons.push('dimensions_outside_standard_range');
  }

  // BASE — qty-tier lookup
  const tier = tiers
    .filter((t) => t.family_id === family.id && t.variant === variant && tierMatch(t, qty))
    .sort((a, b) => b.qty_min - a.qty_min)[0];

  let doorSub = null;
  if (!tier || tier.price == null) {
    confidence = 'unpriced';
    requiresReview = true;
    reasons.push('price_requires_configuration');
  } else {
    doorSub = tier.price;
    breakdown.push({
      stage: 'BASE', step: 'base', component: 'DOOR',
      label: `${family.id} ${variant} qty ${tier.qty_min}–${tier.qty_max ?? '+'}`,
      tierId: tier.id, amount: tier.price, dataStatus: tier.data_status
    });
    if (tier.data_status === 'placeholder') {
      confidence = 'low';
      requiresReview = true;
      reasons.push('base_price_placeholder');
    }
  }

  const opts = ['design', 'glass', 'frame', 'threshold', 'lock', 'hinge', 'sidelight']
    .map((k) => input[k])
    .filter(Boolean)
    .concat(Array.isArray(input.extras) ? input.extras : []);
  const colour = input.colour;

  const chargeMultiplier = (rule) => {
    if (rule.op === 'pct') return null;
    if (rule.unit === 'hinge') return hinges;
    if (rule.charge_scope === 'PER_DOOR_SET') return 1;
    if (rule.charge_scope === 'PER_SIDELIGHT') return Math.max(sidelightCount, 1);
    return doorUnits;
  };

  // OPTION_ADJUSTMENTS — door component only; component_scope 'SIDELIGHT'
  // rules never fire here.
  if (doorSub != null) {
    // Non-standard colour → tiered surcharge; qty ≥ 10 → manual review.
    // Sidelights inherit the door colour — the surcharge is never duplicated
    // per sidelight unless a rule is explicitly scoped PER_SIDELIGHT.
    if (colour && colour !== 'colour_untreated' && !standardOptions.includes(colour)) {
      if (qty >= 10) {
        requiresReview = true;
        if (confidence === 'high') confidence = 'low';
        reasons.push('colour_qty_manual');
      } else {
        const rule = pickRule(rules, { kind: 'option', target: 'colour_nonstandard', familyId: family.id, qty });
        if (rule?.value != null) {
          doorSub = round2(doorSub + rule.value * chargeMultiplier(rule));
          breakdown.push({ stage: 'OPTION_ADJUSTMENTS', step: 'option', component: 'DOOR', label: rule.label || 'Non-standard colour', ruleId: rule.id, op: 'add', value: rule.value, chargeScope: rule.charge_scope, amount: doorSub, dataStatus: rule.data_status });
        }
      }
    }

    for (const opt of opts) {
      // Confirmed glass prices are retail additions, applied once after rounding.
      if (opt === input.glass && glassSurcharge != null) continue;
      const rule = pickRule(rules, {
        kind: 'option', target: opt, familyId: family.id, qty,
        componentScopes: ['DOOR', 'DOOR_LEAF', 'WHOLE_SET']
      });
      if (rule?.value != null) {
        doorSub = rule.op === 'pct'
          ? round2(doorSub * (1 + rule.value / 100))
          : round2(doorSub + rule.value * chargeMultiplier(rule));
        breakdown.push({
          stage: 'OPTION_ADJUSTMENTS', step: 'option', component: 'DOOR', label: rule.label || opt,
          target: opt, ruleId: rule.id, op: rule.op, value: rule.value,
          unit: rule.unit, chargeScope: rule.charge_scope, amount: doorSub, dataStatus: rule.data_status
        });
        if (rule.data_status === 'placeholder') {
          if (confidence === 'high') confidence = 'low';
          requiresReview = true;
          reasons.push(`option_${opt}_placeholder`);
        }
      }
    }

    // Special height (e.g. 2115/2118 mm)
    for (const rule of rules.filter((r) => r.rule_kind === 'dimension' && tierMatch(r, qty)
      && (r.family_id == null || r.family_id === family.id))) {
      const heights = rule.meta?.heights;
      if (Array.isArray(heights) && heights.map(Number).includes(h)) {
        doorSub = round2(doorSub + rule.value * doorUnits);
        breakdown.push({ stage: 'OPTION_ADJUSTMENTS', step: 'dimension', component: 'DOOR', label: rule.label || 'Special height', ruleId: rule.id, op: 'add', value: rule.value, amount: doorSub });
      }
    }

    // Non-standard dimensions surcharge
    if (nonstd) {
      const rule = rules
        .filter((r) => r.rule_kind === 'special_size' && (r.family_id == null || r.family_id === family.id))
        .sort((a, b) => (a.family_id == null ? 1 : 0) - (b.family_id == null ? 1 : 0))[0];
      if (rule?.value != null) {
        doorSub = round2(doorSub * (1 + rule.value / 100));
        breakdown.push({ stage: 'OPTION_ADJUSTMENTS', step: 'special_size', component: 'DOOR', label: rule.label || 'Special dimensions', ruleId: rule.id, op: 'pct', value: rule.value, amount: doorSub });
      }
    }
  }

  // SIDELIGHT components — structural base + effective glass area × €/m² +
  // dividers. An unpriced component → review + withheld retail price.
  let slSub = 0;
  let slOk = true;
  if (sidelightCount > 0) {
    if (!(sl.families || {})[family.id]) {
      requiresReview = true;
      slOk = false;
      reasons.push('sidelight_family_not_supported');
      breakdown.push({ stage: 'SIDELIGHT', step: 'unsupported', component: 'SIDELIGHT', label: `Sidelights not enabled for ${family.id}` });
    } else {
      for (const side of sides) {
        const comp = `SIDELIGHT_${side.toUpperCase()}`;
        const spec = input[`${side}_sidelight`]
          || (input.sidelight_same !== false ? (input.left_sidelight || input.right_sidelight) : null)
          || {};
        const slW = Number(spec.width_mm ?? input.sidelight_width_mm) || null;
        const slH = Number(spec.height_mm ?? input.sidelight_height_mm) || h;
        const glassType = (spec.glass_type || 'CLEAR').toUpperCase();
        const layout = (spec.layout || 'FULL_GLASS').toUpperCase();
        let part = null;
        let outOfRange = false;

        let dividers = LAYOUT_DIVIDERS[layout];
        if (dividers == null) {
          requiresReview = true;
          reasons.push(`sidelight_layout_manual:${layout}`);
          dividers = Number(spec.divider_count) || 0;
        }

        // dimension validation — admin-configured min/max
        const minW = numSetting(slSet, 'min_width_mm');
        const maxW = numSetting(slSet, 'max_width_mm');
        const minH = numSetting(slSet, 'min_height_mm');
        const maxH = numSetting(slSet, 'max_height_mm');
        const standardWidths = (sl.widths || []).filter((x) => x.enabled).map((x) => x.width_mm);
        if (slW == null || slH == null) {
          requiresReview = true; slOk = false;
          reasons.push(`sidelight_dimensions_missing:${side}`);
        } else if (slW < minW || slW > maxW || slH < minH || slH > maxH) {
          requiresReview = true;
          outOfRange = true;
          reasons.push(`sidelight_unsupported_size:${side}`);
          if (!['add', 'pct'].includes(slSet.special_size_op) || slSet.special_size_value == null) slOk = false;
        } else if (!standardWidths.includes(slW) && slSet.custom_width_mode === 'manual') {
          requiresReview = true;
          reasons.push(`sidelight_custom_width:${side}`);
        }

        // effective glass area — documented configurable deductions
        const effW = Math.max(0, (slW || 0) - numSetting(slSet, 'deduction_left_mm') - numSetting(slSet, 'deduction_right_mm')) / 1000;
        const effH = Math.max(0, (slH || 0) - numSetting(slSet, 'deduction_top_mm') - numSetting(slSet, 'deduction_bottom_mm') - dividers * numSetting(slSet, 'deduction_divider_mm')) / 1000;
        const area = Math.round(effW * effH * 10000) / 10000;

        const base = (sl.base || [])
          .filter((b) => b.enabled !== false && (b.family_id === family.id || b.family_id == null))
          .sort((a, b) => (a.family_id == null ? 1 : 0) - (b.family_id == null ? 1 : 0))[0];
        if (base?.base_price != null) part = base.base_price;
        else { requiresReview = true; slOk = false; reasons.push(`sidelight_base_unpriced:${side}`); }
        breakdown.push({
          stage: 'SIDELIGHT', step: 'base', component: comp, label: 'Structural base',
          ruleId: base?.id, widthMm: slW, heightMm: slH,
          amount: base?.base_price ?? null, dataStatus: base?.data_status || 'placeholder'
        });

        const glass = (sl.glass || [])
          .find((g) => g.glass_type === glassType && g.enabled !== false
            && (!g.families || g.families.includes(family.id)));
        if (glass?.price_per_m2 != null) part = round2((part || 0) + area * glass.price_per_m2);
        else { requiresReview = true; slOk = false; reasons.push(`sidelight_glass_unpriced:${glassType}:${side}`); }
        breakdown.push({
          stage: 'SIDELIGHT', step: 'glass', component: comp,
          label: `${glassType} glass ${area} m² @ €/m²`,
          ruleId: glass?.id, glassType, glassAreaM2: area,
          effWidthMm: Math.round(effW * 1000), effHeightMm: Math.round(effH * 1000),
          pricePerM2: glass?.price_per_m2 ?? null,
          amount: glass?.price_per_m2 != null ? round2(area * glass.price_per_m2) : null
        });

        if (dividers > 0) {
          const div = (sl.dividers || []).find((d) => d.divider_type === 'horizontal' && d.enabled !== false);
          if (div && (div.fixed_price != null || div.price_per_lm != null)) {
            const amt = round2((div.fixed_price || 0) * dividers + (div.price_per_lm || 0) * (slW / 1000) * dividers);
            part = round2((part || 0) + amt);
            breakdown.push({ stage: 'SIDELIGHT', step: 'divider', component: comp, label: `${dividers}× horizontal divider`, ruleId: div.id, dividerCount: dividers, amount: amt });
          } else {
            requiresReview = true; slOk = false;
            reasons.push(`sidelight_divider_unpriced:${side}`);
            breakdown.push({ stage: 'SIDELIGHT', step: 'divider', component: comp, label: `${dividers}× horizontal divider`, ruleId: div?.id, dividerCount: dividers, amount: null });
          }
        }

        if (outOfRange && slOk !== false && slSet.special_size_op !== 'manual' && slSet.special_size_value != null) {
          part = slSet.special_size_op === 'add'
            ? round2((part || 0) + slSet.special_size_value)
            : round2((part || 0) * (1 + slSet.special_size_value / 100));
          breakdown.push({ stage: 'SIDELIGHT', step: 'special_size', component: comp, label: 'Sidelight special size', op: slSet.special_size_op, value: slSet.special_size_value, amount: part });
        }

        breakdown.push({ stage: 'SIDELIGHT', step: 'component_subtotal', component: comp, label: `Sidelight subtotal (${side})`, amount: part });
        if (part != null) slSub = round2(slSub + part);
      }
    }
  }

  const subtotal = (doorSub == null || (sidelightCount > 0 && !slOk))
    ? null
    : round2(doorSub + slSub);
  breakdown.push({ stage: 'OPTION_ADJUSTMENTS', step: 'subtotal', label: 'Pricing subtotal (door + sidelights)', amount: subtotal });

  // RETAIL_FACTORS — door part: global → family → variant → option → size;
  // sidelight part: global → sidelight-scope factors. OVERRIDE ends it.
  let doorRaw = doorSub;
  let slRaw = sidelightCount > 0 && slOk ? slSub : null;
  let retailRaw = null;
  if (doorSub != null || slRaw != null) {
    let overridden = false;
    const applicable = factors
      .filter((f) => f.enabled && (
        f.scope === 'global'
        || (f.scope === 'family' && f.scope_key === family.id)
        || (f.scope === 'variant' && f.scope_key === variant)
        || (f.scope === 'option' && (opts.includes(f.scope_key) || f.scope_key === colour))
        || (f.scope === 'size' && nonstd && f.scope_key === 'nonstandard_size')
        || (f.scope === 'sidelight' && sidelightCount > 0)
      ))
      .sort((a, b) => (SCOPE_ORDER[a.scope] - SCOPE_ORDER[b.scope]) || (a.sort - b.sort) || (String(a.id) > String(b.id) ? 1 : -1));

    const applyOp = (raw, f) => round2(
      f.operation === 'MULTIPLIER' ? raw * f.value
        : f.operation === 'PERCENT' ? raw * (1 + f.value / 100)
        : f.operation === 'FIXED_ADD' ? raw + f.value
        : f.operation === 'FIXED_SUBTRACT' ? raw - f.value
        : f.value // OVERRIDE
    );

    for (const factor of applicable) {
      if (overridden) {
        breakdown.push({ stage: 'RETAIL_FACTORS', step: 'factor_skipped', label: factor.label || factor.scope, reason: 'override_applied' });
        continue;
      }
      const toDoor = factor.scope !== 'sidelight';
      const toSl = factor.scope === 'global' || factor.scope === 'sidelight';
      if (factor.operation === 'OVERRIDE') {
        if (toDoor && doorRaw != null) doorRaw = factor.value;
        if (toSl && slRaw != null) slRaw = factor.value;
        overridden = true;
      } else {
        if (toDoor && doorRaw != null) doorRaw = applyOp(doorRaw, factor);
        if (toSl && slRaw != null) slRaw = applyOp(slRaw, factor);
      }
      breakdown.push({
        stage: 'RETAIL_FACTORS', step: 'factor',
        label: factor.label || `${factor.scope}:${factor.scope_key || ''}`,
        factorId: factor.id, scope: factor.scope, scopeKey: factor.scope_key,
        operation: factor.operation, value: factor.value,
        amount: round2((doorRaw || 0) + (slRaw || 0))
      });
    }
    retailRaw = (doorRaw == null || (sidelightCount > 0 && slRaw == null))
      ? null
      : round2((doorRaw || 0) + (slRaw || 0));
  }

  // RETAIL_OVERRIDE — a direct public price replaces the calculated one.
  let hasOverride = false;
  if (subtotal != null) {
    const ov = (overrides || [])
      .filter((o) => o.active && o.family_id === family.id && o.variant === variant && tierMatch(o, qty))
      .sort((a, b) => (b.qty_min - a.qty_min) || (String(b.id) > String(a.id) ? 1 : -1))[0];
    if (ov) {
      hasOverride = true;
      retailRaw = ov.price;
      breakdown.push({ stage: 'RETAIL_FACTORS', step: 'retail_override', label: 'B2C override — replaces calculated retail', overrideId: ov.id, amount: ov.price });
    }
  }

  // ROUNDING
  let retailPrice = null;
  if (retailRaw != null) {
    const step = priceList.rounding_step;
    retailPrice = hasOverride || priceList.rounding_mode === 'NONE' || !step || step <= 0
      ? (hasOverride ? retailRaw : round2(retailRaw))
      : priceList.rounding_mode === 'UP'
        ? Math.ceil(retailRaw / step) * step
        : Math.round(retailRaw / step) * step;
    breakdown.push({
      stage: 'ROUNDING', step: 'rounding',
      label: `Rounding ${priceList.rounding_mode} ${step}`,
      mode: priceList.rounding_mode, stepSize: step,
      rawAmount: retailRaw, delta: round2(retailPrice - retailRaw), amount: retailPrice
    });
  }

  if (glassSurcharge != null) {
    if (retailPrice != null) retailPrice = round2(retailPrice + glassSurcharge);
    breakdown.push({ stage: 'RETAIL_GLASS', step: 'glass', component: 'DOOR',
      target: input.glass, chargeScope: 'PER_DOOR_SET', value: glassSurcharge, amount: retailPrice });
  }

  return {
    glassSurcharge,
    ok: true,
    family: family.id,
    variant,
    configurationType,
    qty,
    sidelightCount,
    retailPrice,
    totalPrice: retailPrice == null ? null : round2(retailPrice * qty),
    currency: priceList.currency,
    confidence,
    requiresReview,
    reasons,
    priceList: { code: priceList.code, version: priceList.version },
    subtotalNet: subtotal,
    breakdown
  };
}

function pickRule(rules, { kind, target, familyId, qty, componentScopes }) {
  return rules
    .filter((r) => r.rule_kind === kind && r.target === target
      && (r.family_id == null || r.family_id === familyId)
      && (!componentScopes || componentScopes.includes(r.component_scope || 'DOOR'))
      && tierMatch(r, qty))
    .sort((a, b) => ((a.family_id == null ? 1 : 0) - (b.family_id == null ? 1 : 0))
      || ((b.qty_min || 0) - (a.qty_min || 0)))[0];
}
